import React, { useState } from 'react';
import {
    ListItem,
    Avatar,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    Box,
    Divider,
    ListItemIcon,
    Collapse
} from '@mui/material';
import { AccountCircle, Lock, PowerSettingsNew, Person, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useAuthStore, useSettingsStore } from '@core/store';
import { useAuth } from '@core/auth/AuthProvider';
import { useTranslation } from 'react-i18next';

interface Language {
    name: string;
    icon: string;
    locale: string;
}

const languages: Language[] = [
    {
        name: 'german',
        icon: 'de',
        locale: 'de-DE'
    },
    {
        name: 'english',
        icon: 'en',
        locale: 'en'
    }
];

interface UserBlockProps {
    className?: string;
}

export const UserMenuBlock: React.FC<UserBlockProps> = ({ className }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [languageExpanded, setLanguageExpanded] = useState(false);
    const { user, hasRole } = useAuthStore();
    const { selectedLocale, setLocale } = useSettingsStore();
    const { logout } = useAuth();
    const { t } = useTranslation();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            setAnchorEl(null);
            setLanguageExpanded(false);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setLanguageExpanded(false);
    };

    const handleLanguageToggle = (event: React.MouseEvent) => {
        event.stopPropagation();
        setLanguageExpanded(!languageExpanded);
    };

    const handleLogout = async () => {
        handleClose();
        await logout();
    };

    const changeLanguage = (language: Language) => {
        setLocale(language);
        handleClose();
    };

    return (
        <ListItem className={`sidebar-profile ${className || ''}`} onClick={handleClick} sx={{ cursor: 'pointer' }}>
            <Avatar sx={{ mr: 2, background: 'none !important' }}>
                <AccountCircle sx={{ fontSize: 35, color: 'white' }} />
            </Avatar>
            <ListItemText
                primary={
                    <Typography variant="body2" sx={{ color: 'white' }}>
                        {user?.fullName || 'User'}
                    </Typography>
                }
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} className="userblock-dropdown">
                <Box sx={{ p: 2, borderBottom: 'none !important' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {user?.fullName || 'User'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {user?.email || 'user@example.com'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                        Role: {user?.role}
                    </Typography>
                </Box>

                {/* Language Button with Expand/Collapse */}
                <MenuItem onClick={handleLanguageToggle}>
                    <ListItemIcon>
                        <img
                            src={`/images/flags/${languages.find((lang) => lang.locale === selectedLocale.locale)?.icon || 'en'}.png`}
                            alt="Language"
                            style={{ width: 24, height: 16, objectFit: 'cover' }}
                        />
                    </ListItemIcon>
                    <ListItemText>{t('languages')}</ListItemText>
                    {languageExpanded ? <ExpandLess /> : <ExpandMore />}
                </MenuItem>

                {/* Expandable Language Options */}
                <Collapse in={languageExpanded} timeout="auto" unmountOnExit>
                    {languages.map((language) => (
                        <MenuItem
                            key={language.locale}
                            onClick={() => changeLanguage(language)}
                            selected={selectedLocale.locale === language.locale}
                            sx={{
                                pl: 6,
                                backgroundColor: 'transparent !important'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: '40px' }}>
                                <img
                                    src={`/images/flags/${language.icon}.png`}
                                    alt={language.name}
                                    style={{ width: 24, height: 16, objectFit: 'cover' }}
                                />
                            </ListItemIcon>
                            <ListItemText>{t(language.name)}</ListItemText>
                        </MenuItem>
                    ))}
                </Collapse>

                <Divider />

                {/* Sign out */}
                <MenuItem onClick={handleLogout} className="logout-menu-item">
                    <ListItemIcon>
                        <PowerSettingsNew />
                    </ListItemIcon>
                    <ListItemText>{t('signOut')}</ListItemText>
                </MenuItem>
            </Menu>
        </ListItem>
    );
};
